import React, { useContext, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Header,
  Icon,
  Message,
  Accordion,
  AccordionTitle,
  AccordionContent,
  Label,
} from "semantic-ui-react";
import { GET_STUDENT_ANNOUNCEMENTS, MARK_REPORT_AS_READ } from "../GraphQL";
import Attachment from "./Attachment";
import UserContext from "contexts/UserContext";
import moment from "moment";

const getSubject = (announcement) => {
  const subjectField = announcement.fields.find((f) => f.label === "Subject");
  return subjectField?.value || "Untitled Announcement";
};

const getMessage = (announcement) => {
  const messageField = announcement.fields.find((f) => f.label === "Message");
  return messageField?.value || "";
};

const StudentAnnouncements = ({ studentId }) => {
  const user = useContext(UserContext);
  const [activeIndex, setActiveIndex] = useState(-1);

  const { loading, error, data } = useQuery(GET_STUDENT_ANNOUNCEMENTS, {
    variables: { studentId, limit: 10 },
  });

  const [markReportAsRead] = useMutation(MARK_REPORT_AS_READ);

  if (loading || error) {
    if (error) console.error("Error loading announcements:", error);
    return null;
  }

  const announcements = data?.getStudent?.announcements?.reports || [];
  const isProTeacher = !!data?.getStudent?.myTeacher?.isProUser;

  if (announcements.length === 0 && !isProTeacher) {
    return null;
  }

  const handleClick = (_, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);

    const announcement = announcements[index];
    if (newIndex !== -1 && announcement && !announcement.read && user.isParent) {
      markReportAsRead({ variables: { report: announcement.id } });
    }
  };

  return (
    <div style={{ marginBottom: "2em" }}>
      <Header as="h3" dividing>
        <Header.Content>Class Announcements</Header.Content>
      </Header>

      {announcements.length === 0 ? (
        <Message info>
          <Message.Content>No announcements yet.</Message.Content>
        </Message>
      ) : (
        <Accordion styled fluid>
          {announcements.map((announcement, index) => (
            <React.Fragment key={announcement.id}>
              <AccordionTitle
                active={activeIndex === index}
                index={index}
                onClick={handleClick}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Icon name="dropdown" />
                <strong>{getSubject(announcement)}</strong>
                {user.isParent && !announcement.read ? (
                  <Label size="tiny" color="teal" style={{ marginLeft: "0.5em" }}>
                    New
                  </Label>
                ) : null}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: "0.9em",
                    color: "#999",
                  }}
                >
                  {moment(announcement.date).format("MMM D, YYYY")}
                </span>
              </AccordionTitle>
              <AccordionContent active={activeIndex === index}>
                <p style={{ whiteSpace: "pre-wrap", marginBottom: "1em" }}>
                  {getMessage(announcement)}
                </p>
                {announcement.files && announcement.files.length > 0 ? (
                  <div>
                    {announcement.files.map((file) => (
                      <Attachment key={file.key} file={file} />
                    ))}
                  </div>
                ) : null}
              </AccordionContent>
            </React.Fragment>
          ))}
        </Accordion>
      )}
    </div>
  );
};

export default StudentAnnouncements;
